import { useEffect, useState } from 'react';

import type { StudyMetrics } from '@/model/Study.model';
import Http from '@/support/http';
import { RouteService } from '@/support/RouteService';

// No subjects state here; subjects come from Redux in Recaging

/**
 * Interface for study cage
 */
interface StudyCage {
  name: string;
}

/**
 * Custom hook for fetching recaging data
 */
export const useRecagingData = (studyId: string) => {
  const [studyCages, setStudyCages] = useState<StudyCage[]>([]);
  const [nextCage, setNextCage] = useState<number>();
  const [loading, setLoading] = useState(true);
  const [studyCagesLoading, setStudyCagesLoading] = useState(true);
  const [apiError, setApiError] = useState<Error | boolean>(false);

  useEffect(() => {
    const fetchStudyMetrics = async () => {
      try {
        const { url } = RouteService.legacyApi({
          apiRoute: 'studies.metrics',
          path: { id: studyId },
          query: { include: 'counts' },
        });

        const { data } = await Http.get<{ data: StudyMetrics }>(url.href);
        setNextCage(data.data.counts.cages_with_trashed + 1);
      } catch (error) {
        setApiError(error as Error);
      } finally {
        setLoading(false);
      }
    };

    const fetchStudyCages = async () => {
      try {
        const studyCagesUrl = RouteService.legacyApi({
          apiRoute: 'studies.collections',
          query: {
            sort: 'id',
            order: 'asc',
            filterType: 'and',
            'filters[]=': `study|eq|${studyId}`,
            perPage: '-1',
          },
        });

        const { data } = await Http.get<{ data: StudyCage[] }>(studyCagesUrl.url.href);
        setStudyCages(data.data);
      } catch (error) {
        setApiError(error as Error);
      } finally {
        setStudyCagesLoading(false);
      }
    };

    if (studyId) {
      fetchStudyMetrics();
      fetchStudyCages();
    }
  }, [studyId]);

  return {
    studyCages,
    nextCage,
    loading,
    studyCagesLoading,
    apiError,
  };
};
